/**
 * Mock Data Generators for Preview Mode
 * 
 * Provides realistic mock data for unauthenticated users to preview
 * the portfolio and referrals pages without exposing real user data.
 */

import type { MarketSymbol } from "@/solana/constants";

// Mock Portfolio Data
export const getMockPortfolioData = () => {
  return {
    accountSummary: {
      collateral: 128327.31,
      availableBalance: 72450.00,
      usedMargin: 27550.00,
      totalPnl: 12847.50,
      unrealizedPnl: 4325.00,
      realizedPnl: 8522.50,
      marginRatio: 0.28,
      accountLeverage: 2.1,
    },
    positions: [
      {
        id: "mock-pos-1",
        market: "BTC-PERP" as MarketSymbol,
        side: "long" as const,
        size: 0.5,
        entryPrice: 94500.0,
        currentPrice: 96200.0,
        leverage: 3,
        liquidationPrice: 82500.0,
        unrealizedPnl: 850.0,
        realizedPnl: 0,
        margin: 15750.0,
        status: "open" as const,
        openedAt: new Date(Date.now() - 3600000),
        updatedAt: new Date(),
        timestamp: Date.now(),
      },
      {
        id: "mock-pos-2",
        market: "ETH-PERP" as MarketSymbol,
        side: "long" as const,
        size: 8.0,
        entryPrice: 3425.75,
        currentPrice: 3550.0,
        leverage: 2,
        liquidationPrice: 2850.0,
        unrealizedPnl: 994.0,
        realizedPnl: 0,
        margin: 13703.0,
        status: "open" as const,
        openedAt: new Date(Date.now() - 7200000),
        updatedAt: new Date(),
        timestamp: Date.now(),
      },
      {
        id: "mock-pos-3",
        market: "SOL-PERP" as MarketSymbol,
        side: "short" as const,
        size: 150,
        entryPrice: 132.50,
        currentPrice: 128.80,
        leverage: 2,
        liquidationPrice: 155.0,
        unrealizedPnl: 555.0,
        realizedPnl: 0,
        margin: 9937.5,
        status: "open" as const,
        openedAt: new Date(Date.now() - 86400000),
        updatedAt: new Date(),
        timestamp: Date.now(),
      },
    ],
    fills: [
      {
        id: "mock-fill-1",
        order_id: "mock-order-fill-1",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 3600000).toISOString(),
        market: "BTC-PERP" as MarketSymbol,
        side: "buy" as const,
        size: "0.5",
        price: "94500.00",
        fee: "47.25",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
      {
        id: "mock-fill-2",
        order_id: "mock-order-fill-2",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 7200000).toISOString(),
        market: "ETH-PERP" as MarketSymbol,
        side: "buy" as const,
        size: "8.0",
        price: "3425.75",
        fee: "27.41",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
      {
        id: "mock-fill-3",
        order_id: "mock-order-fill-3",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 86400000).toISOString(),
        market: "SOL-PERP" as MarketSymbol,
        side: "sell" as const,
        size: "150",
        price: "132.50",
        fee: "19.88",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
      {
        id: "mock-fill-4",
        order_id: "mock-order-fill-4",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 172800000).toISOString(),
        market: "BTC-PERP" as MarketSymbol,
        side: "sell" as const,
        size: "0.3",
        price: "93200.00",
        fee: "27.96",
        realized_pnl: "845.50",
        fill_type: "full_close" as const,
      },
      {
        id: "mock-fill-5",
        order_id: "mock-order-fill-5",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 259200000).toISOString(),
        market: "ETH-PERP" as MarketSymbol,
        side: "sell" as const,
        size: "5.0",
        price: "3380.00",
        fee: "16.90",
        realized_pnl: "1240.75",
        fill_type: "partial_close" as const,
      },
      {
        id: "mock-fill-6",
        order_id: "mock-order-fill-6",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 345600000).toISOString(),
        market: "SOL-PERP" as MarketSymbol,
        side: "buy" as const,
        size: "200",
        price: "128.30",
        fee: "25.66",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
      {
        id: "mock-fill-7",
        order_id: "mock-order-fill-7",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 432000000).toISOString(),
        market: "BTC-PERP" as MarketSymbol,
        side: "buy" as const,
        size: "0.4",
        price: "91800.00",
        fee: "36.72",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
      {
        id: "mock-fill-8",
        order_id: "mock-order-fill-8",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 518400000).toISOString(),
        market: "ETH-PERP" as MarketSymbol,
        side: "buy" as const,
        size: "6.5",
        price: "3295.50",
        fee: "21.42",
        realized_pnl: "0",
        fill_type: "open" as const,
      },
    ],
    fundingPayments: [
      {
        id: "mock-funding-1",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 28800000).toISOString(),
        market: "BTC-PERP",
        position_size: "0.25",
        funding_rate: "0.0001",
        payment: "-1.13",
      },
      {
        id: "mock-funding-2",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 57600000).toISOString(),
        market: "ETH-PERP",
        position_size: "3.5",
        funding_rate: "0.00008",
        payment: "-0.69",
      },
    ],
    orders: [
      {
        id: "mock-order-1",
        user_id: "mock-user",
        created_at: new Date(Date.now() - 1800000).toISOString(),
        updated_at: new Date(Date.now() - 1800000).toISOString(),
        market: "BTC-PERP",
        side: "buy" as const,
        order_type: "limit" as const,
        size: "0.1",
        filled_size: "0",
        price: "45000.00",
        status: "open" as const,
      },
    ],
    deposits: [
      {
        id: "mock-deposit-1",
        user_id: "mock-user",
        deposit_address: "mock-deposit-addr-1",
        tx_signature: "mock-tx-1234567890",
        token_mint: "USDC",
        amount: "10000.00",
        amount_usd: "10000.00",
        status: "credited" as const,
        created_at: new Date(Date.now() - 172800000).toISOString(),
        credited_at: new Date(Date.now() - 172700000).toISOString(),
      },
      {
        id: "mock-deposit-2",
        user_id: "mock-user",
        deposit_address: "mock-deposit-addr-1",
        tx_signature: "mock-tx-0987654321",
        token_mint: "USDC",
        amount: "5000.00",
        amount_usd: "5000.00",
        status: "credited" as const,
        created_at: new Date(Date.now() - 604800000).toISOString(),
        credited_at: new Date(Date.now() - 604700000).toISOString(),
      },
    ],
    withdrawals: [
      {
        id: "mock-withdrawal-1",
        user_id: "mock-user",
        destination_address: "mockaddr1234567890",
        amount: "2500.00",
        amount_usd: "2500.00",
        token_mint: "USDC",
        tx_signature: "mock-tx-withdraw-123",
        status: "completed" as const,
        auto_approved: true,
        rejection_reason: null,
        created_at: new Date(Date.now() - 259200000).toISOString(),
        approved_at: new Date(Date.now() - 259100000).toISOString(),
        processed_at: new Date(Date.now() - 259000000).toISOString(),
      },
    ],
    pnlHistory: {
      summary: {
        totalPnl: 1247.89,
        realizedPnl: 847.89,
        unrealizedPnl: 400.00,
        totalFees: 156.43,
        totalFunding: -45.23,
        netPnl: 1046.23,
        change24h: 125.50,
        change7d: 487.30,
        change30d: 1247.89,
      },
      dailyData: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 86400000).toISOString().split('T')[0],
        pnl: Math.random() * 200 - 50,
        fees: Math.random() * 20,
        funding: Math.random() * 10 - 5,
      })),
    },
  };
};

// Mock Referrals Data
export const getMockReferralsData = () => {
  return {
    code: {
      ref_code: "PREVIEW",
      custom_code: null,
      user_id: "mock-user",
      created_at: new Date(Date.now() - 2592000000).toISOString(),
    },
    stats: {
      total_referrals: 12,
      total_earnings: "847.50",
      claimable_balance: "247.50",
      total_volume_usd: "1847500.00",
      active_referrals: 8,
    },
    referrals: [
      {
        id: "mock-ref-1",
        wallet_address: "DemoUser7Xk9...3F8m",
        email: null,
        created_at: new Date(Date.now() - 1209600000).toISOString(),
        total_volume_usd: "385000.00",
        total_commissions: "192.50",
        last_trade_at: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: "mock-ref-2",
        wallet_address: null,
        email: "t***r@example.com",
        created_at: new Date(Date.now() - 1728000000).toISOString(),
        total_volume_usd: "250000.00",
        total_commissions: "125.00",
        last_trade_at: new Date(Date.now() - 172800000).toISOString(),
      },
      {
        id: "mock-ref-3",
        wallet_address: "Demo5Trader...9K2p",
        email: null,
        created_at: new Date(Date.now() - 2160000000).toISOString(),
        total_volume_usd: "180000.00",
        total_commissions: "90.00",
        last_trade_at: new Date(Date.now() - 259200000).toISOString(),
      },
      {
        id: "mock-ref-4",
        wallet_address: null,
        email: "c***o@example.com",
        created_at: new Date(Date.now() - 2592000000).toISOString(),
        total_volume_usd: "145000.00",
        total_commissions: "72.50",
        last_trade_at: new Date(Date.now() - 345600000).toISOString(),
      },
      {
        id: "mock-ref-5",
        wallet_address: "DemoWhale...5T7q",
        email: null,
        created_at: new Date(Date.now() - 3024000000).toISOString(),
        total_volume_usd: "520000.00",
        total_commissions: "260.00",
        last_trade_at: new Date(Date.now() - 432000000).toISOString(),
      },
      {
        id: "mock-ref-6",
        wallet_address: null,
        email: "j***e@example.com",
        created_at: new Date(Date.now() - 3456000000).toISOString(),
        total_volume_usd: "98000.00",
        total_commissions: "49.00",
        last_trade_at: new Date(Date.now() - 518400000).toISOString(),
      },
      {
        id: "mock-ref-7",
        wallet_address: "DemoScalp...2H8k",
        email: null,
        created_at: new Date(Date.now() - 3888000000).toISOString(),
        total_volume_usd: "72000.00",
        total_commissions: "36.00",
        last_trade_at: new Date(Date.now() - 604800000).toISOString(),
      },
      {
        id: "mock-ref-8",
        wallet_address: null,
        email: "m***h@example.com",
        created_at: new Date(Date.now() - 4320000000).toISOString(),
        total_volume_usd: "55000.00",
        total_commissions: "27.50",
        last_trade_at: new Date(Date.now() - 691200000).toISOString(),
      },
    ],
  };
};

// Mock Affiliate Data (for future use)
export const getMockAffiliateData = () => {
  return {
    affiliate: {
      id: "mock-affiliate-1",
      user_id: "mock-user",
      custom_code: "PREVIEWAFF",
      status: "active",
      commission_rate_maker: 0.25,
      commission_rate_taker: 0.25,
      created_at: new Date(Date.now() - 7776000000).toISOString(),
    },
    stats: {
      totalReferrals: 47,
      activeReferrals: 32,
      totalEarnings: 3847.25,
      pendingPayout: 1247.50,
      totalVolume: 12475000.00,
      conversionRate: 0.68,
    },
  };
};

/**
 * Mock Trading Analysis Fills for Performance Lab Pro™ Preview
 * 
 * Generates ~70 completed trades over 30 days with:
 * - 63% win rate
 * - Profit factor ~2.1
 * - Mixed BTC, ETH, SOL trades
 * - Realistic P&L curve
 */
export const getMockTradingAnalysisFills = () => {
  const fills: Array<{
    id: string;
    user_id: string;
    market: string;
    side: "buy" | "sell";
    size: string;
    price: string;
    fee: string;
    realized_pnl: string;
    fill_type: string;
    created_at: string;
  }> = [];

  // Seeded random for consistency (but still varies slightly with time)
  const seed = Math.floor(Date.now() / 86400000); // Changes daily
  const seededRandom = (i: number) => {
    const x = Math.sin(seed + i * 9999) * 10000;
    return x - Math.floor(x);
  };

  // Market configs with realistic prices
  const markets = [
    { symbol: "BTC-PERP", basePrice: 96500, volatility: 0.015, minSize: 0.01, maxSize: 0.5 },
    { symbol: "ETH-PERP", basePrice: 3450, volatility: 0.02, minSize: 0.5, maxSize: 10 },
    { symbol: "SOL-PERP", basePrice: 195, volatility: 0.025, minSize: 5, maxSize: 200 },
  ];

  // Generate 70 complete trades (140 fills total)
  const totalTrades = 70;
  const winCount = 44; // ~63% win rate
  const lossCount = totalTrades - winCount;
  
  // Create a shuffled array of win/loss outcomes
  const outcomes: boolean[] = [
    ...Array(winCount).fill(true),
    ...Array(lossCount).fill(false),
  ];
  
  // Shuffle outcomes using seeded random
  for (let i = outcomes.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(i * 100) * (i + 1));
    [outcomes[i], outcomes[j]] = [outcomes[j], outcomes[i]];
  }

  // Time distribution over 30 days
  const now = Date.now();
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  
  // Generate trades
  let fillId = 1;
  
  for (let tradeIdx = 0; tradeIdx < totalTrades; tradeIdx++) {
    const isWin = outcomes[tradeIdx];
    const marketIdx = Math.floor(seededRandom(tradeIdx * 3) * markets.length);
    const market = markets[marketIdx];
    
    // Random time within 30 days, weighted toward more recent
    const timeOffset = thirtyDaysMs * Math.pow(seededRandom(tradeIdx * 7), 1.5);
    const openTime = now - timeOffset;
    
    // Trade duration: 15 minutes to 8 hours
    const durationMs = (15 + seededRandom(tradeIdx * 11) * 465) * 60 * 1000;
    const closeTime = openTime + durationMs;
    
    // Random side
    const isLong = seededRandom(tradeIdx * 13) > 0.5;
    const openSide = isLong ? "buy" : "sell";
    const closeSide = isLong ? "sell" : "buy";
    
    // Size calculation
    const sizeRange = market.maxSize - market.minSize;
    const size = market.minSize + seededRandom(tradeIdx * 17) * sizeRange * 0.6;
    const sizeStr = size.toFixed(market.symbol === "BTC-PERP" ? 3 : market.symbol === "ETH-PERP" ? 2 : 1);
    
    // Price calculation with some variation
    const priceVariation = (seededRandom(tradeIdx * 19) - 0.5) * market.basePrice * market.volatility;
    const entryPrice = market.basePrice + priceVariation;
    
    // P&L calculation: wins average $950-1500, losses average $80-200
    // This gives ~$36,724 total net P&L
    let pnlAmount: number;
    if (isWin) {
      pnlAmount = 950 + seededRandom(tradeIdx * 23) * 550;
    } else {
      pnlAmount = -(80 + seededRandom(tradeIdx * 29) * 120);
    }
    
    // Calculate exit price from P&L
    const pnlPerUnit = pnlAmount / size;
    const exitPrice = isLong 
      ? entryPrice + pnlPerUnit 
      : entryPrice - pnlPerUnit;
    
    // Fee calculation (0.05% of notional)
    const entryNotional = entryPrice * size;
    const exitNotional = exitPrice * size;
    const entryFee = entryNotional * 0.0005;
    const exitFee = exitNotional * 0.0005;
    
    // Net realized P&L after fees
    const realizedPnl = pnlAmount - entryFee - exitFee;
    
    // Open fill
    fills.push({
      id: `mock-analysis-fill-${fillId++}`,
      user_id: "mock-user",
      market: market.symbol,
      side: openSide as "buy" | "sell",
      size: sizeStr,
      price: entryPrice.toFixed(2),
      fee: entryFee.toFixed(2),
      realized_pnl: "0",
      fill_type: "open",
      created_at: new Date(openTime).toISOString(),
    });
    
    // Close fill
    fills.push({
      id: `mock-analysis-fill-${fillId++}`,
      user_id: "mock-user",
      market: market.symbol,
      side: closeSide as "buy" | "sell",
      size: sizeStr,
      price: exitPrice.toFixed(2),
      fee: exitFee.toFixed(2),
      realized_pnl: realizedPnl.toFixed(2),
      fill_type: "full_close",
      created_at: new Date(closeTime).toISOString(),
    });
  }
  
  // Sort by created_at (oldest first)
  fills.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  
  return fills;
};

